import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Workflow, Inbox, ListChecks, AlertTriangle, CheckCircle2, Play,
  ShoppingCart, Users, FileText, Briefcase, Sparkles, Clock, X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatDateTime } from '@/lib/api'

type Aba = 'home' | 'tarefas' | 'processos'

interface CampoSchema {
  chave: string
  label: string
  tipo: 'texto' | 'texto_longo' | 'numero' | 'select' | 'data' | 'anexo' | 'pessoa' | 'produto'
  opcoes?: string[]
  obrigatorio?: boolean
}

export type { CampoSchema }

interface Processo {
  id: number
  codigo: string
  nome: string
  descricao?: string
  categoria?: string
  icone?: string
  cor?: string
  ativo: boolean
  form_abertura?: { campos?: CampoSchema[] }
  sla_dias?: number
  nodes: any[]
}

interface Tarefa {
  id: number
  instancia_id: number
  instancia_titulo?: string
  instancia_numero?: number
  processo_nome?: string
  node_codigo?: string
  node_nome?: string
  node_form_schema?: { campos?: CampoSchema[] }
  prazo?: string
  aberta_em: string
}

const ICONES: Record<string, any> = {
  ShoppingCart, Users, FileText, Briefcase, Sparkles, Workflow,
}

const CORES: Record<string, string> = {
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  rose: 'bg-rose-100 text-rose-700 border-rose-200',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
}

function getCor(c?: string) {
  return CORES[c || ''] || CORES.slate
}

function getIcone(nome?: string) {
  return ICONES[nome || ''] || Workflow
}

export default function BPMPage() {
  const [aba, setAba] = useState<Aba>('home')
  const [abrirProc, setAbrirProc] = useState<Processo | null>(null)

  const { data: dashboard } = useQuery<any>({
    queryKey: ['bpm-dashboard'],
    queryFn: async () => (await api.get('/bpm/dashboard')).data,
  })

  const { data: processos = [] } = useQuery<Processo[]>({
    queryKey: ['bpm-processos'],
    queryFn: async () => (await api.get('/bpm/processos')).data,
  })

  const { data: tarefas = [] } = useQuery<Tarefa[]>({
    queryKey: ['bpm-minhas-tarefas'],
    queryFn: async () => (await api.get('/bpm/minhas-tarefas')).data,
  })

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Workflow className="w-7 h-7 text-brand-600" />
        <div>
          <h1 className="text-2xl font-bold">BPM</h1>
          <p className="text-sm text-ink-500">Processos e fluxos de trabalho da empresa</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-ink-200 mb-6">
        {[
          { id: 'home', label: 'Visão geral', icon: Workflow },
          { id: 'tarefas', label: `Minhas tarefas${dashboard?.minhas_tarefas ? ` (${dashboard.minhas_tarefas})` : ''}`, icon: Inbox },
          { id: 'processos', label: 'Processos', icon: ListChecks },
        ].map(t => {
          const Ic = t.icon as any
          const ativo = aba === t.id
          return (
            <button key={t.id} onClick={() => setAba(t.id as Aba)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition ${
                ativo ? 'border-brand-600 text-brand-700' : 'border-transparent text-ink-500 hover:text-ink-800'
              }`}>
              <Ic className="w-4 h-4" /> {t.label}
            </button>
          )
        })}
      </div>

      {aba === 'home' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Em andamento" valor={dashboard?.abertas ?? '-'} icon={Play} cor="blue" />
            <MetricCard label="Atrasados" valor={dashboard?.atrasadas ?? '-'} icon={AlertTriangle} cor="rose" />
            <MetricCard label="Minhas tarefas" valor={dashboard?.minhas_tarefas ?? '-'} icon={Inbox} cor="amber" />
            <MetricCard label="Concluídos 30d" valor={dashboard?.concluidas_30d ?? '-'} icon={CheckCircle2} cor="emerald" />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-ink-600 mb-3 uppercase tracking-wide">Iniciar um processo</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {processos.filter(p => p.ativo).map(p => {
                const Ic = getIcone(p.icone)
                return (
                  <button key={p.id} onClick={() => setAbrirProc(p)}
                    className="card card-body text-left hover:border-brand-500 hover:shadow-md transition group">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${getCor(p.cor)}`}>
                        <Ic className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{p.nome}</div>
                        <div className="text-xs text-ink-500 mt-0.5">{p.descricao}</div>
                        {p.sla_dias && <div className="text-[10px] text-ink-400 mt-1">SLA: {p.sla_dias}d</div>}
                      </div>
                    </div>
                  </button>
                )
              })}
              {processos.length === 0 && (
                <div className="col-span-full text-center py-8 text-ink-500 text-sm">
                  Nenhum processo cadastrado. Vá em "Processos" pra criar.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {aba === 'tarefas' && (
        <TarefasPanel tarefas={tarefas} />
      )}

      {aba === 'processos' && (
        <ProcessosPanel processos={processos} />
      )}

      {abrirProc && <AbrirInstanciaModal proc={abrirProc} onClose={() => setAbrirProc(null)} />}
    </div>
  )
}

function MetricCard({ label, valor, icon: Ic, cor }: any) {
  return (
    <div className={`card card-body border ${getCor(cor)}`}>
      <div className="flex items-center gap-3">
        <Ic className="w-6 h-6" />
        <div>
          <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
          <div className="text-2xl font-bold">{valor}</div>
        </div>
      </div>
    </div>
  )
}

function TarefasPanel({ tarefas }: { tarefas: Tarefa[] }) {
  const nav = useNavigate()
  if (tarefas.length === 0) {
    return (
      <div className="card card-body text-center py-12 text-ink-500">
        <Inbox className="w-10 h-10 mx-auto mb-2 text-ink-300" />
        Nenhuma tarefa pendente pra você 🎉
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {tarefas.map(t => {
        const vencido = t.prazo && new Date(t.prazo) < new Date()
        return (
          <button key={t.id} onClick={() => nav(`/bpm/instancias/${t.instancia_id}`)}
            className={`card card-body w-full text-left hover:border-brand-500 transition flex items-center gap-4 ${
              vencido ? 'border-rose-300' : ''
            }`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{t.node_nome}</span>
                {vencido && <span className="badge-danger text-[10px]">ATRASADO</span>}
              </div>
              <div className="text-xs text-ink-500 mt-0.5">
                {t.processo_nome} · {t.instancia_titulo} · aberta {formatDateTime(t.aberta_em)}
              </div>
            </div>
            {t.prazo && (
              <div className={`text-xs ${vencido ? 'text-rose-600' : 'text-ink-500'} flex items-center gap-1`}>
                <Clock className="w-3 h-3" /> {formatDateTime(t.prazo)}
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

function ProcessosPanel({ processos }: { processos: Processo[] }) {
  const nav = useNavigate()
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button onClick={() => nav('/bpm/processos/novo')} className="btn-primary">
          + Novo processo
        </button>
      </div>
      {processos.map(p => {
        const Ic = getIcone(p.icone)
        return (
          <button key={p.id} onClick={() => nav(`/bpm/processos/${p.id}/editar`)}
            className="card card-body w-full text-left hover:border-brand-500 transition flex items-center gap-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${getCor(p.cor)}`}>
              <Ic className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-semibold">{p.nome} <span className="text-xs text-ink-400 ml-2">{p.codigo}</span></div>
              <div className="text-xs text-ink-500">{p.descricao} · {p.nodes?.length || 0} nós · SLA {p.sla_dias ?? '-'}d</div>
            </div>
            <span className={`badge ${p.ativo ? 'badge-success' : 'badge-neutral'}`}>
              {p.ativo ? 'Ativo' : 'Inativo'}
            </span>
          </button>
        )
      })}
      {processos.length === 0 && (
        <div className="card card-body text-center py-8 text-ink-500 text-sm">
          Nenhum processo cadastrado.
        </div>
      )}
    </div>
  )
}

function AbrirInstanciaModal({ proc, onClose }: { proc: Processo; onClose: () => void }) {
  const qc = useQueryClient()
  const nav = useNavigate()
  const [titulo, setTitulo] = useState('')
  const [dados, setDados] = useState<Record<string, any>>({})
  const campos = proc.form_abertura?.campos || []

  const criar = useMutation({
    mutationFn: async () => (await api.post('/bpm/instancias', {
      processo_id: proc.id, titulo: titulo || undefined, dados,
    })).data,
    onSuccess: (r) => {
      toast.success(`${proc.nome} #${r.numero} aberto`)
      qc.invalidateQueries({ queryKey: ['bpm-dashboard'] })
      onClose()
      nav(`/bpm/instancias/${r.id}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const Ic = getIcone(proc.icone)

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-ink-200">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${getCor(proc.cor)}`}>
              <Ic className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold">{proc.nome}</div>
              <div className="text-xs text-ink-500">{proc.descricao}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="label">Título <span className="text-ink-400">(opcional)</span></label>
            <input className="input" value={titulo} onChange={e => setTitulo(e.target.value)}
              placeholder={`Ex.: ${proc.nome} — Projeto X`} />
          </div>

          <CamposForm campos={campos} valores={dados} setValores={setDados} />
        </div>

        <div className="p-4 border-t border-ink-200 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={() => criar.mutate()} disabled={criar.isPending} className="btn-primary">
            {criar.isPending ? 'Abrindo...' : 'Abrir processo'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function CamposForm({ campos, valores, setValores }: {
  campos: CampoSchema[]; valores: Record<string, any>; setValores: (v: any) => void
}) {
  if (campos.length === 0) return <div className="text-xs text-ink-400">Sem campos pra preencher.</div>
  return (
    <>
      {campos.map(c => (
        <CampoInput key={c.chave} campo={c}
          valor={valores[c.chave]}
          onChange={(v) => setValores({ ...valores, [c.chave]: v })} />
      ))}
    </>
  )
}

function CampoInput({ campo, valor, onChange }: { campo: CampoSchema; valor: any; onChange: (v: any) => void }) {
  return (
    <div>
      <label className="label">
        {campo.label}
        {campo.obrigatorio && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {campo.tipo === 'texto' && (
        <input className="input" value={valor || ''} onChange={e => onChange(e.target.value)} />
      )}
      {campo.tipo === 'texto_longo' && (
        <textarea className="input min-h-[72px]" value={valor || ''} onChange={e => onChange(e.target.value)} />
      )}
      {campo.tipo === 'numero' && (
        <input type="number" className="input" value={valor ?? ''}
          onChange={e => onChange(e.target.value ? Number(e.target.value) : null)} />
      )}
      {campo.tipo === 'select' && (
        <select className="input" value={valor || ''} onChange={e => onChange(e.target.value)}>
          <option value="">Selecione...</option>
          {campo.opcoes?.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {campo.tipo === 'data' && (
        <input type="date" className="input" value={valor || ''} onChange={e => onChange(e.target.value)} />
      )}
      {campo.tipo === 'anexo' && (
        <AnexoInput valor={valor} onChange={onChange} />
      )}
      {campo.tipo === 'pessoa' && (
        <ReferenciaInput tipo="pessoa" valor={valor} onChange={onChange} />
      )}
      {campo.tipo === 'produto' && (
        <ReferenciaInput tipo="produto" valor={valor} onChange={onChange} />
      )}
    </div>
  )
}

function AnexoInput({ valor, onChange }: { valor: any; onChange: (v: any) => void }) {
  const [uploading, setUploading] = useState(false)
  async function upload(file: File) {
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await api.post('/bpm/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      onChange({ nome: file.name, url: r.data.url, size: file.size })
    } catch {
      toast.error('Falha no upload')
    } finally {
      setUploading(false)
    }
  }
  return (
    <div className="space-y-1">
      {valor?.url && (
        <div className="text-xs bg-ink-50 px-2 py-1 rounded flex items-center justify-between">
          <a href={valor.url} target="_blank" rel="noreferrer" className="text-brand-600 underline">{valor.nome}</a>
          <button onClick={() => onChange(null)} className="text-ink-400 hover:text-rose-500 ml-2">✕</button>
        </div>
      )}
      <input type="file" className="input" disabled={uploading}
        onChange={e => e.target.files?.[0] && upload(e.target.files[0])} />
    </div>
  )
}

function ReferenciaInput({ tipo, valor, onChange }: { tipo: 'pessoa' | 'produto'; valor: any; onChange: (v: any) => void }) {
  const endpoint = tipo === 'pessoa' ? '/cadastros/pessoas' : '/cadastros/produtos'
  const { data: opcoes = [] } = useQuery<any[]>({
    queryKey: [`ref-${tipo}`],
    queryFn: async () => (await api.get(endpoint)).data,
  })
  const label = (o: any) => tipo === 'pessoa' ? o.nome : `${o.codigo} — ${o.descricao}`
  return (
    <select className="input" value={valor?.id || ''}
      onChange={e => {
        const id = Number(e.target.value) || null
        const found = opcoes.find((o: any) => o.id === id)
        onChange(found ? { id: found.id, label: label(found) } : null)
      }}>
      <option value="">Selecione...</option>
      {opcoes.map((o: any) => <option key={o.id} value={o.id}>{label(o)}</option>)}
    </select>
  )
}
